import React, { useEffect, useRef, useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import * as Fa from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchData, postData } from "../../../Services/GlobalService";
import Loader from "../../../Components/Loader";

export interface ConstructOption {
  label: string;
  value: string;
}

export interface ExceptionRow {
  ConstructSeq: number;
  DB_CODE: string;
  CompName: string;
  Extra_PC_Points: number | null;
  Total_Points: number | null;
  Adjusted_Points: number | null;
  TurnOver: number | null;
  Discount_Percentage: number | null;
  Payout: number | null;
  REMARKS: string | null;
}


const ExceptionHandling: React.FC = () => {
  const userId = localStorage.getItem("UserId")?.toString();

  const controllerName = "DBScoreCard/LBMBrandAndBrandMapping";
  const controllerExcpName = "ExceptionHandlling";
  const Endpoint = {
    FetchingConstructData: "FetchingConstructData",
    SaveExceptionData: "SaveExceptionData",
    GetAllExceptionData: "GetAllExceptionData",
  };
  const EndPointCustSeg = {
  GetFilterDistributor: "GetFilterDistributor",
  GetAllExcelData: "GetAllExcelData",
};

  const [selectedConstruct, setSelectedConstruct] = useState<any>(null);
  const [gridData, setGridData] = useState<ExceptionRow[]>([]);
  const [allGridData, setAllGridData] = useState<any>([]);
  const [constructList, setConstructList] =
    useState<any>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [generateLoading, setGenerateLoading] = useState<boolean>(false);

  useEffect(() => {
    const loadConstructs = async () => {
      try {
        const res = await fetchData(
          controllerName,
          Endpoint.FetchingConstructData,
          `ChannelType=B2C`,
        );
        setConstructList(
          res.Data.map((d: any) => ({ label: d.Name, value: d.Seq })) ?? [],
        );
      } catch (error: any) {
        toast.error(
          `${error?.response?.data?.Message ?? "Failed to load constructs"}`,
        );
      }
    };
    loadConstructs();
  }, []);

  useEffect(() => {
    if (!selectedConstruct) {
      setGridData([]);
      return;
    }
    console.log("selectedConstruct", selectedConstruct);
    fetchExceptionData(selectedConstruct);
  }, [selectedConstruct]);

  const fetchExceptionData = async (constructId: string) => {
    setLoader(true);
    try {
      const res = await fetchData(
        controllerExcpName,
        Endpoint.GetAllExceptionData,
        // `ConstructSeq=6`
        `ConstructSeq=${constructId}`,
      );
      setGridData(res.Data.AllExceptionHandData ?? []);
      setAllGridData(res.Data);
    } catch (error: any) {
      toast.error(
        `${error?.response?.data?.Message ?? "Failed to fetch data"}`,
      );
    } finally {
      setLoader(false);
    }
  };

  const onFieldChange = (
    value: any,
    rowData: ExceptionRow,
    field: keyof ExceptionRow,
  ) => {
    const updated = gridData.map((item) =>
      item.DB_CODE === rowData.DB_CODE ? { ...item, [field]: value } : item,
    );
    setGridData(updated);
  };

  const handleSave = async () => {
    if (!selectedConstruct) {
      toast.warn("Please select a construct.");
      return;
    }
    if (gridData.length === 0) {
      toast.warn("No data to save.");
      return;
    }
    setSaveLoading(true);
    try {
      const payload = {
        ConstructId: selectedConstruct,
        UserId: userId,
        ExceptionData: gridData,
      };
      console.log("[ExceptionHandling] Save payload", payload);
      // const res = await postData(controllerName, Endpoint.SaveExceptionData, payload);
      // toast.success(res.Message);
      toast.success("Data saved successfully.");
    } catch (error: any) {
      toast.error(`${error?.response?.data?.Message ?? "Save failed"}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleGenerate = () => {
    if (!selectedConstruct) {
      toast.warn("Please select a construct.");
      return;
    }

    // Use gridData (reflects live user edits to Adjusted_Points / REMARKS)
    const currentGridData = gridData;
    const thresholdData = allGridData?.ThresholdData || [];

    if (currentGridData.length === 0) {
      toast.warn("No data available to generate.");
      return;
    }

    // Validation — Adjusted_Points must be a valid number (not null / undefined / "")
    const blankRow = currentGridData.find(
      (item) =>
        item.Adjusted_Points === null ||
        item.Adjusted_Points === undefined ||
        item.Adjusted_Points === ("" as any),
    );

    if (blankRow) {
      toast.error(
        `Available Points cannot be blank for DB: ${blankRow.DB_CODE} - ${blankRow.CompName}`,
      );
      return;
    }

    if (thresholdData.length === 0) {
      toast.error("Threshold data is missing. Cannot calculate payout.");
      return;
    }

    try {
      setGenerateLoading(true);

      // Extract threshold values
      const threshold = thresholdData[0];
      const CnsThreshold    = Number(threshold.Threshold_Point);
      const CnsTargetPoints = Number(threshold.Target_Point);
      const CnsStretchPoint = Number(threshold.Stretch_Point);
      const CnsThresholdPer    = Number(threshold.Threshold_Percentage);
      const CnsTargetPointsPer = Number(threshold.Target_Percentage);
      const CnsStretchPointPer = Number(threshold.Stretch_Percentage);

      // Calculate Discount_Percentage and Payout for each row
      const updatedData: ExceptionRow[] = currentGridData.map((row) => {
        const adjustedPoints = Number(row.Adjusted_Points);
        const turnover       = Number(row.TurnOver);

        let discountPer = 0;

        if (adjustedPoints < CnsThreshold) {
          discountPer = 0;
        } else if (adjustedPoints === CnsThreshold) {
          discountPer = CnsThresholdPer;
        } else if (adjustedPoints > CnsThreshold && adjustedPoints <= CnsTargetPoints) {
          discountPer =
            CnsThresholdPer +
            ((CnsTargetPointsPer - CnsThresholdPer) / (CnsTargetPoints - CnsThreshold)) *
              (adjustedPoints - CnsThreshold);
        } else if (adjustedPoints > CnsTargetPoints && adjustedPoints <= CnsStretchPoint) {
          discountPer =
            CnsTargetPointsPer +
            ((CnsStretchPointPer - CnsTargetPointsPer) / (CnsStretchPoint - CnsTargetPoints)) *
              (adjustedPoints - CnsTargetPoints);
        } else {
          // adjustedPoints > CnsStretchPoint → cap at stretch percentage
          discountPer = CnsStretchPointPer;
        }

        return {
          ...row,
          Discount_Percentage: Number(discountPer.toFixed(2)),
          Payout: Number(((discountPer / 100) * turnover).toFixed(4)),
        };
      });

      setGridData(updatedData);
      setAllGridData((prev: any) => ({ ...prev, AllExceptionHandData: updatedData }));
      toast.success("Payout calculated successfully.");
    } catch (error: any) {
      toast.error(
        `${error?.response?.data?.Message ?? "Failed to generate data. Please try again."}`,
      );
    } finally {
      setGenerateLoading(false);
    }
  };

  console.log(gridData,"gridData");

  const availablePointsBody = (rowData: ExceptionRow) => (
    <InputNumber
      value={rowData.Adjusted_Points}
      onValueChange={(e) => onFieldChange(e.value, rowData, "Adjusted_Points")}
      maxFractionDigits={2}
      min={0}
      className="w-100"
    />
  );

  const remarksBody = (rowData: ExceptionRow) => (
    <InputText
      value={rowData.REMARKS ?? ""}
      onChange={(e) => onFieldChange(e.target.value, rowData, "REMARKS")}
      className="w-100"
      placeholder="Enter remarks"
    />
  );

  if (loader) return <Loader />;

  return (
    <main>
      <div className="col-12 mb-5">
        <div className="row">
          <div className="col-lg-12 col-md-12 col-sm-12">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mx-0">
                <li className="breadcrumb-item">DB SIP</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Exception Handling
                </li>
              </ol>
            </nav>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <h3 className="py-2">Exception Handling</h3>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="text-end">
              <button
                type="button"
                className="btn anime-btn"
                onClick={handleSave}
                disabled={saveLoading}
              >
                {saveLoading ? (
                  <>
                    Saving...{" "}
                    <span
                      className="spinner-border spinner-border-sm ms-1"
                      role="status"
                      aria-hidden="true"
                    />
                  </>
                ) : (
                  <>Save</>
                )}
              </button>
            </div>
          </div>
        </div>

        <section>
          <div className="sales-box">
            <div className="row d-flex justify-content-end align-items-end mb-3">
              <div className="col-lg-4 col-md-6 col-sm-12">
                <div className="d-block">
                  <label className="form-label">Select Construct Type</label>
                  <div className="d-flex align-items-center gap-2">
                    <Dropdown
                      value={selectedConstruct}
                      onChange={(e) => setSelectedConstruct(e.value)}
                      options={constructList}
                      optionLabel="label"
                      placeholder="Select Construct Here"
                      className="w-100"
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-8 col-md-6 col-sm-12">
                <div className="text-end">
                  <button
                    type="button"
                    className="btn anime-secondary-btn"
                    onClick={handleGenerate}
                    disabled={generateLoading}
                  >
                    {generateLoading ? (
                      <>
                        Generating...{" "}
                        <span
                          className="spinner-border spinner-border-sm ms-1"
                          role="status"
                          aria-hidden="true"
                        />
                      </>
                    ) : (
                      <>
                        Generate <Fa.FaSync className="ms-2" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* ── DataTable ── */}
            <div className="col-lg-12 col-md-12 col-sm-12">
              <div className="DataTable">
                <DataTable
                  value={gridData}
                  rows={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  filterDisplay="row"
                  showGridlines
                  paginator
                  stripedRows
                  scrollable
                  scrollHeight="60vh"
                  tableStyle={{ minWidth: "60rem" }}
                  emptyMessage={
                    selectedConstruct
                      ? "No exceptions found for the selected construct."
                      : "Please select a construct to view exceptions"
                  }
                >
                  <Column
                    field="DB_CODE"
                    header="DB Code"
                    filter
                    filterMatchMode="contains"
                    filterPlaceholder="Search Here..."
                    style={{ minWidth: "120px" }}
                  />
                  <Column
                    field="CompName"
                    header="DB Name"
                    filter
                    filterMatchMode="contains"
                    filterPlaceholder="Search Here..."
                    style={{ minWidth: "180px" }}
                  />
                  <Column
                    field="Adjusted_Points"
                    header="Available Points"
                    body={availablePointsBody}
                    style={{ minWidth: "160px" }}
                  />
                  <Column field="Discount_Percentage" header="% Discount" />
                  <Column field="TurnOver" header="Turnover" />
                  <Column field="Payout" header="Payout" />
                  <Column
                    field="REMARKS"
                    header="Remarks"
                    body={remarksBody}
                    style={{ minWidth: "200px" }}
                  />
                </DataTable>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default ExceptionHandling;
